import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Clock, User, MapPin, Shield,
  FileText, Target, CheckCircle, Search as SearchIcon, Plus,
  Loader2, Edit, ChevronRight, Activity, Microscope, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const severityConfig = {
  low: { bg: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  medium: { bg: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  high: { bg: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  critical: { bg: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500' },
};
const statusConfig = {
  open: { bg: 'bg-blue-100 text-blue-700', label: 'Open', step: 0 },
  investigating: { bg: 'bg-yellow-100 text-yellow-700', label: 'Investigating', step: 1 },
  action_required: { bg: 'bg-red-100 text-red-700', label: 'Action Required', step: 2 },
  closed: { bg: 'bg-green-100 text-green-700', label: 'Closed', step: 3 },
};
const harmLabels = {
  no_harm: 'No Harm', near_miss: 'Near Miss', temporary_minor: 'Temporary Minor Harm',
  temporary_major: 'Temporary Major Harm', permanent_harm: 'Permanent Harm', death: 'Death',
};
const rcaMethodologies = ['5 Why Analysis', 'Fishbone Diagram', 'FMEA', 'Swiss Cheese Model', 'Barrier Analysis', 'Change Analysis', 'Other'];

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [rcas, setRcas] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRcaForm, setShowRcaForm] = useState(false);
  const [showActionForm, setShowActionForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiActionsLoading, setAiActionsLoading] = useState(false);
  const [aiActionSuggestions, setAiActionSuggestions] = useState(null);
  const [acceptingAction, setAcceptingAction] = useState(null);
  const [rcaForm, setRcaForm] = useState({ methodology: '', findings: '', root_causes: '', recommendations: '' });
  const [actionForm, setActionForm] = useState({ title: '', description: '', priority: 'medium', assigned_to_name: '', department: '', due_date: '' });

  const loadData = async () => {
    setLoading(true);
    try {
      const [ev, rcaList, actionList] = await Promise.all([
        api.get(`/api/safety-events/${eventId}`),
        api.get(`/api/safety-events/${eventId}/rca`),
        api.get(`/api/corrective-actions?event_id=${eventId}`),
      ]);
      setEvent(ev);
      setRcas(rcaList);
      setActions(actionList);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [eventId]);

  const handleCreateRca = async () => {
    if (!rcaForm.methodology) { toast.error('Select a methodology'); return; }
    setSubmitting(true);
    try {
      await api.post(`/api/safety-events/${eventId}/rca`, {
        ...rcaForm,
        root_causes: rcaForm.root_causes.split('\n').filter(Boolean),
        status: 'in_progress',
      });
      toast.success('RCA investigation started');
      setShowRcaForm(false);
      setRcaForm({ methodology: '', findings: '', root_causes: '', recommendations: '' });
      setAiSuggestions(null);
      loadData();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const askNikoForRca = async () => {
    setAiLoading(true);
    setAiSuggestions(null);
    try {
      const result = await api.post(`/api/safety-events/${eventId}/ai-rca-suggest`, {});
      if (result.success && result.suggestions) {
        const s = result.suggestions;
        setAiSuggestions(s);
        // Map AI suggested methodology to closest dropdown option
        const methodologyMap = rcaMethodologies.find(m =>
          (s.suggested_methodology || '').toLowerCase().includes(m.toLowerCase().split(' ')[0].toLowerCase())
        ) || '';
        // Auto-fill form with AI suggestions
        setRcaForm(prev => ({
          methodology: methodologyMap || prev.methodology,
          findings: s.findings_summary || prev.findings,
          root_causes: (s.probable_root_causes || []).join('\n'),
          recommendations: (s.recommendations || []).join('\n'),
        }));
        toast.success('Niko analyzed the event and filled in suggestions');
      }
    } catch (e) {
      toast.error('AI analysis failed: ' + e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateAction = async () => {
    if (!actionForm.title) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/corrective-actions', { ...actionForm, event_id: eventId, action_type: 'corrective' });
      toast.success('Action plan created');
      setShowActionForm(false);
      setActionForm({ title: '', description: '', priority: 'medium', assigned_to_name: '', department: '', due_date: '' });
      loadData();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const askNikoForActions = async () => {
    setAiActionsLoading(true);
    setAiActionSuggestions(null);
    try {
      const result = await api.post(`/api/safety-events/${eventId}/ai-action-suggest`, {});
      if (result.success && result.suggestions) {
        setAiActionSuggestions(result.suggestions);
        toast.success(`Niko generated ${result.suggestions.actions?.length || 0} action plan suggestions`);
      }
    } catch (e) {
      toast.error('AI analysis failed: ' + e.message);
    } finally {
      setAiActionsLoading(false);
    }
  };

  const acceptAiAction = async (aiAction, index) => {
    setAcceptingAction(index);
    try {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + (aiAction.timeline_days || 14));
      await api.post('/api/corrective-actions', {
        event_id: eventId,
        title: aiAction.title,
        description: aiAction.description,
        action_type: aiAction.action_type || 'corrective',
        priority: aiAction.priority || 'medium',
        department: aiAction.department || '',
        assigned_to_name: '',
        due_date: dueDate.toISOString(),
      });
      toast.success(`Action "${aiAction.title.substring(0, 40)}..." created`);
      // Remove from suggestions
      setAiActionSuggestions(prev => ({
        ...prev,
        actions: prev.actions.filter((_, i) => i !== index),
      }));
      loadData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAcceptingAction(null);
    }
  };

  const acceptAllAiActions = async () => {
    if (!aiActionSuggestions?.actions?.length) return;
    setAcceptingAction(-1);
    try {
      for (const aiAction of aiActionSuggestions.actions) {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + (aiAction.timeline_days || 14));
        await api.post('/api/corrective-actions', {
          event_id: eventId,
          title: aiAction.title,
          description: aiAction.description,
          action_type: aiAction.action_type || 'corrective',
          priority: aiAction.priority || 'medium',
          department: aiAction.department || '',
          assigned_to_name: '',
          due_date: dueDate.toISOString(),
        });
      }
      toast.success(`${aiActionSuggestions.actions.length} action plans created`);
      setAiActionSuggestions(null);
      loadData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAcceptingAction(null);
    }
  };

  const completeRca = async (rcaId) => {
    try {
      await api.put(`/api/rca-investigations/${rcaId}`, { status: 'completed' });
      toast.success('RCA marked as completed');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const completeAction = async (actionId) => {
    try {
      await api.put(`/api/corrective-actions/${actionId}`, { status: 'completed' });
      toast.success('Action marked as completed');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>;
  if (!event) return <div className="text-center py-24 text-gray-400">Event not found</div>;

  const sev = severityConfig[event.severity] || severityConfig.medium;
  const stat = statusConfig[event.status] || statusConfig.open;
  const currentStep = stat.step;

  // Build timeline entries
  const timeline = [];
  timeline.push({
    type: 'reported', icon: AlertTriangle, color: 'bg-blue-500',
    title: 'Event Reported',
    subtitle: `by ${event.reported_by_name}`,
    time: event.created_at,
    detail: event.description,
    completed: true,
  });
  if (rcas.length > 0) {
    rcas.forEach(rca => {
      timeline.push({
        type: 'rca', icon: Microscope, color: rca.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500',
        title: `RCA: ${rca.methodology}`,
        subtitle: `Led by ${rca.lead_investigator}`,
        time: rca.created_at,
        detail: rca.findings,
        completed: rca.status === 'completed',
        data: rca,
      });
    });
  }
  actions.forEach(action => {
    timeline.push({
      type: 'action', icon: Target, color: action.status === 'completed' ? 'bg-green-500' : action.status === 'overdue' ? 'bg-red-500' : 'bg-blue-500',
      title: action.title,
      subtitle: `Assigned: ${action.assigned_to_name || 'Unassigned'}`,
      time: action.created_at,
      detail: action.description,
      completed: action.status === 'completed',
      data: action,
    });
  });
  if (event.status === 'closed') {
    timeline.push({
      type: 'closed', icon: CheckCircle, color: 'bg-green-500',
      title: 'Event Closed',
      subtitle: event.resolution_notes || 'Resolved',
      time: event.updated_at,
      completed: true,
    });
  }

  const completedActions = actions.filter(a => a.status === 'completed').length;
  const totalActions = actions.length;
  const completedRcas = rcas.filter(r => r.status === 'completed').length;

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      {/* Back button */}
      <button onClick={() => navigate('/hospital/safety-events')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm transition-colors" data-testid="back-to-events">
        <ArrowLeft className="w-4 h-4" /> Back to Safety Events
      </button>

      {/* Event Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${sev.bg}`}>{event.severity}</span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${stat.bg}`}>{stat.label}</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{event.category}</span>
              {event.harm_level && <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">{harmLabels[event.harm_level] || event.harm_level}</span>}
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-testid="event-title">{event.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.location}</span>
              {event.department && <span className="flex items-center gap-1"><Activity className="w-3.5 h-3.5" />{event.department}</span>}
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{new Date(event.created_at).toLocaleString()}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setShowRcaForm(true)} className="gap-1.5 text-sm" data-testid="start-rca-btn">
              <Microscope className="w-4 h-4" /> Start RCA
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowActionForm(true)} className="gap-1.5 text-sm" data-testid="add-action-btn">
              <Target className="w-4 h-4" /> Add Action
            </Button>
            <Button
              size="sm"
              onClick={askNikoForActions}
              disabled={aiActionsLoading}
              className="gap-1.5 text-sm bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] text-white hover:opacity-90"
              data-testid="ai-generate-actions-btn"
            >
              {aiActionsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              {aiActionsLoading ? 'Generating...' : 'AI Action Plans'}
            </Button>
          </div>
        </div>

        {/* Journey Progress Bar */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between relative">
            {/* Background line */}
            <div className="absolute top-5 left-6 right-6 h-0.5 bg-gray-200" />
            <div className="absolute top-5 left-6 h-0.5 bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] transition-all" style={{ width: `${(currentStep / 3) * (100 - 8)}%` }} />
            {[
              { label: 'Reported', step: 0, icon: AlertTriangle },
              { label: 'Investigating', step: 1, icon: Microscope },
              { label: 'Action Required', step: 2, icon: Target },
              { label: 'Closed', step: 3, icon: CheckCircle },
            ].map(s => (
              <div key={s.step} className="flex flex-col items-center z-10 relative">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  s.step <= currentStep ? 'bg-[var(--niko-teal)] border-[var(--niko-teal)] text-white' : 'bg-white border-gray-200 text-gray-400'
                }`}>
                  <s.icon className="w-4.5 h-4.5" />
                </div>
                <span className={`text-xs mt-1.5 font-medium ${s.step <= currentStep ? 'text-[var(--niko-teal)]' : 'text-gray-400'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Action Suggestions Panel */}
      {aiActionSuggestions && aiActionSuggestions.actions?.length > 0 && (
        <div className="bg-gradient-to-r from-[var(--niko-teal)]/5 to-[var(--niko-green)]/5 rounded-2xl border border-[var(--niko-teal)]/20 p-6" data-testid="ai-actions-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Niko's Recommended Action Plans</h2>
                <p className="text-xs text-gray-500">{aiActionSuggestions.actions.length} actions suggested. Review and accept individually or all at once.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={acceptAllAiActions} disabled={acceptingAction !== null} className="gap-1.5 bg-[var(--niko-teal)] text-white hover:bg-[var(--niko-teal)]/90" data-testid="accept-all-actions-btn">
                {acceptingAction === -1 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                Accept All
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAiActionSuggestions(null)} data-testid="dismiss-ai-actions-btn">
                Dismiss
              </Button>
            </div>
          </div>

          {aiActionSuggestions.overall_strategy && (
            <p className="text-sm text-gray-600 mb-4 p-3 bg-white/60 rounded-lg italic">{aiActionSuggestions.overall_strategy}</p>
          )}

          <div className="space-y-3">
            {aiActionSuggestions.actions.map((action, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 transition-all hover:shadow-md" data-testid="ai-action-suggestion">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                        action.priority === 'critical' ? 'bg-red-100 text-red-700 border-red-200' :
                        action.priority === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                        action.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                        'bg-blue-100 text-blue-700 border-blue-200'
                      }`}>{action.priority}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-600">{action.action_type}</span>
                      {action.department && <span className="text-xs text-gray-500">{action.department}</span>}
                      {action.timeline_days && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{action.timeline_days} days</span>}
                    </div>
                    <h3 className="font-medium text-gray-900 text-sm">{action.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{action.description}</p>
                    {action.rationale && <p className="text-xs text-[var(--niko-teal)] mt-1 italic">Rationale: {action.rationale}</p>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => acceptAiAction(action, i)}
                    disabled={acceptingAction !== null}
                    className="gap-1 bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
                    data-testid="accept-action-btn"
                  >
                    {acceptingAction === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content: Timeline + Details */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 space-y-0">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Event Journey</h2>
          <div className="relative" data-testid="event-timeline">
            {/* Timeline line */}
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

            {timeline.map((entry, i) => (
              <div key={i} className="relative pl-14 pb-8 last:pb-0 group">
                {/* Timeline dot */}
                <div className={`absolute left-3 top-1 w-5 h-5 rounded-full ${entry.color} flex items-center justify-center ring-4 ring-white`}>
                  {entry.completed && <CheckCircle className="w-3 h-3 text-white" />}
                </div>

                <div className={`bg-white rounded-xl border p-4 transition-all hover:shadow-md ${
                  entry.completed ? 'border-gray-100' : 'border-l-4 border-l-[var(--niko-teal)] border-gray-100'
                }`} data-testid={`timeline-entry-${entry.type}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <entry.icon className={`w-4 h-4 ${entry.completed ? 'text-green-600' : 'text-gray-500'}`} />
                        <h3 className="font-medium text-gray-900 text-sm">{entry.title}</h3>
                      </div>
                      <p className="text-xs text-gray-400">{entry.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{new Date(entry.time).toLocaleDateString()}</span>
                      {entry.type === 'rca' && !entry.completed && (
                        <button onClick={() => completeRca(entry.data.id)} className="text-xs text-green-600 hover:underline font-medium" data-testid="complete-rca-btn">Complete</button>
                      )}
                      {entry.type === 'action' && !entry.completed && (
                        <button onClick={() => completeAction(entry.data.id)} className="text-xs text-green-600 hover:underline font-medium" data-testid="complete-action-btn">Complete</button>
                      )}
                    </div>
                  </div>
                  {entry.detail && <p className="text-sm text-gray-600 mt-2 leading-relaxed">{entry.detail}</p>}

                  {/* RCA details */}
                  {entry.type === 'rca' && entry.data && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                      {entry.data.root_causes?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Root Causes Identified:</p>
                          <ul className="space-y-1">
                            {entry.data.root_causes.map((rc, j) => (
                              <li key={j} className="flex items-start gap-1.5 text-sm text-gray-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                                {rc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {entry.data.recommendations && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Recommendations:</p>
                          <p className="text-sm text-gray-700 whitespace-pre-line">{entry.data.recommendations}</p>
                        </div>
                      )}
                      {entry.data.contributing_factors?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {entry.data.contributing_factors.map(f => (
                            <span key={f} className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">{f}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action details */}
                  {entry.type === 'action' && entry.data && (
                    <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        entry.data.priority === 'critical' ? 'bg-red-50 text-red-600' :
                        entry.data.priority === 'high' ? 'bg-orange-50 text-orange-600' :
                        entry.data.priority === 'medium' ? 'bg-yellow-50 text-yellow-600' : 'bg-blue-50 text-blue-600'
                      }`}>{entry.data.priority} priority</span>
                      {entry.data.department && <span>{entry.data.department}</span>}
                      {entry.data.due_date && <span>Due: {new Date(entry.data.due_date).toLocaleDateString()}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Add more nodes placeholder */}
            {event.status !== 'closed' && (
              <div className="relative pl-14">
                <div className="absolute left-3 top-1 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center ring-4 ring-white">
                  <Plus className="w-3 h-3 text-gray-400" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowRcaForm(true)} className="text-sm text-[var(--niko-teal)] hover:underline font-medium">Start RCA</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={() => setShowActionForm(true)} className="text-sm text-[var(--niko-teal)] hover:underline font-medium">Add Action</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar: Event Details + Stats */}
        <div className="space-y-4">
          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Resolution Progress</h3>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">RCA Investigations</span>
                  <span className="text-xs font-medium text-gray-900">{completedRcas}/{rcas.length}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--niko-teal)] rounded-full transition-all" style={{ width: rcas.length > 0 ? `${(completedRcas / rcas.length) * 100}%` : '0%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">Action Plans</span>
                  <span className="text-xs font-medium text-gray-900">{completedActions}/{totalActions}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: totalActions > 0 ? `${(completedActions / totalActions) * 100}%` : '0%' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Event Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Event Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between"><span className="text-gray-500">Reported By</span><span className="font-medium text-gray-900">{event.reported_by_name}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Location</span><span className="font-medium text-gray-900">{event.location}</span></div>
              {event.department && <div className="flex items-center justify-between"><span className="text-gray-500">Department</span><span className="font-medium text-gray-900">{event.department}</span></div>}
              {event.time_of_incident && <div className="flex items-center justify-between"><span className="text-gray-500">Time</span><span className="font-medium text-gray-900">{event.time_of_incident}</span></div>}
              {event.discovery_method && <div className="flex items-center justify-between"><span className="text-gray-500">Discovery</span><span className="font-medium text-gray-900">{event.discovery_method}</span></div>}
              <div className="flex items-center justify-between"><span className="text-gray-500">Patient Involved</span><span className={`font-medium ${event.patient_involved ? 'text-red-600' : 'text-gray-900'}`}>{event.patient_involved ? 'Yes' : 'No'}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Patient Harm</span><span className={`font-medium ${event.patient_harm ? 'text-red-600' : 'text-gray-900'}`}>{event.patient_harm ? 'Yes' : 'No'}</span></div>
              {event.patient_age && <div className="flex items-center justify-between"><span className="text-gray-500">Patient Age</span><span className="font-medium text-gray-900">{event.patient_age}</span></div>}
              {event.patient_gender && <div className="flex items-center justify-between"><span className="text-gray-500">Patient Gender</span><span className="font-medium text-gray-900">{event.patient_gender}</span></div>}
            </div>
          </div>

          {/* Contributing Factors */}
          {event.contributing_factors?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Contributing Factors</h3>
              <div className="flex flex-wrap gap-1.5">
                {event.contributing_factors.map(f => (
                  <span key={f} className="px-2.5 py-1 bg-[var(--niko-teal)]/10 text-[var(--niko-teal)] rounded-lg text-xs font-medium">{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Witnesses */}
          {event.witnesses?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Witnesses</h3>
              <div className="space-y-2">
                {event.witnesses.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <User className="w-3.5 h-3.5 text-gray-400" />{w}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Immediate Actions */}
          {event.immediate_actions && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Immediate Actions Taken</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{event.immediate_actions}</p>
            </div>
          )}
        </div>
      </div>

      {/* RCA Create Form */}
      {showRcaForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowRcaForm(false); setAiSuggestions(null); }} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rca-form-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold">Start RCA Investigation</h2>
              <button onClick={() => { setShowRcaForm(false); setAiSuggestions(null); }} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {/* Ask Niko AI Banner */}
            <div className="px-6 pt-5 pb-0">
              <div className={`rounded-xl p-4 transition-all ${aiSuggestions ? 'bg-gradient-to-r from-[var(--niko-teal)]/5 to-[var(--niko-green)]/5 border border-[var(--niko-teal)]/20' : 'bg-gradient-to-r from-gray-50 to-gray-50 border border-gray-100'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Ask Niko - AI Root Cause Analysis</p>
                      <p className="text-xs text-gray-500">{aiSuggestions ? 'Suggestions applied below. Review and edit before saving.' : 'Let AI analyze this event and suggest root causes'}</p>
                    </div>
                  </div>
                  <Button
                    onClick={askNikoForRca}
                    disabled={aiLoading}
                    variant={aiSuggestions ? 'outline' : 'default'}
                    size="sm"
                    className={`gap-2 ${!aiSuggestions ? 'bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] text-white hover:opacity-90' : ''}`}
                    data-testid="ask-niko-rca-btn"
                  >
                    {aiLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                    ) : aiSuggestions ? (
                      <><Shield className="w-4 h-4" /> Re-analyze</>
                    ) : (
                      <><Shield className="w-4 h-4" /> Ask Niko</>
                    )}
                  </Button>
                </div>

                {/* AI Insights (shown after analysis) */}
                {aiSuggestions && (
                  <div className="mt-3 pt-3 border-t border-[var(--niko-teal)]/10 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">Methodology:</span>
                      <span className="text-xs font-semibold text-[var(--niko-teal)]">{aiSuggestions.suggested_methodology}</span>
                    </div>
                    {aiSuggestions.methodology_rationale && (
                      <p className="text-xs text-gray-500 italic">{aiSuggestions.methodology_rationale}</p>
                    )}
                    {aiSuggestions.risk_level && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500">Recurrence Risk:</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          aiSuggestions.risk_level === 'critical' ? 'bg-red-100 text-red-700' :
                          aiSuggestions.risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                          aiSuggestions.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>{aiSuggestions.risk_level}</span>
                      </div>
                    )}
                    {aiSuggestions.contributing_factors?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="text-xs font-medium text-gray-500 mr-1">AI-identified factors:</span>
                        {aiSuggestions.contributing_factors.map(f => (
                          <span key={f} className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">{f}</span>
                        ))}
                      </div>
                    )}
                    {aiSuggestions.similar_event_patterns && (
                      <p className="text-xs text-gray-500 mt-1"><span className="font-medium">Pattern insight:</span> {aiSuggestions.similar_event_patterns}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Methodology *</Label>
                <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={rcaForm.methodology} onChange={e => setRcaForm({...rcaForm, methodology: e.target.value})} data-testid="rca-methodology-select">
                  <option value="">Select methodology...</option>
                  {rcaMethodologies.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label>Findings</Label>
                <Textarea className="mt-1" rows={4} value={rcaForm.findings} onChange={e => setRcaForm({...rcaForm, findings: e.target.value})} placeholder="Describe findings..." data-testid="rca-findings-input" />
              </div>
              <div>
                <Label>Root Causes (one per line)</Label>
                <Textarea className="mt-1" rows={4} value={rcaForm.root_causes} onChange={e => setRcaForm({...rcaForm, root_causes: e.target.value})} placeholder="Root cause 1&#10;Root cause 2..." data-testid="rca-causes-input" />
              </div>
              <div>
                <Label>Recommendations</Label>
                <Textarea className="mt-1" rows={4} value={rcaForm.recommendations} onChange={e => setRcaForm({...rcaForm, recommendations: e.target.value})} placeholder="Recommended actions..." />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => { setShowRcaForm(false); setAiSuggestions(null); }} className="flex-1">Cancel</Button>
              <Button onClick={handleCreateRca} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-rca-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Start Investigation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Action Create Form */}
      {showActionForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowActionForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="action-form-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add Corrective Action</h2>
              <button onClick={() => setShowActionForm(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Title *</Label><Input className="mt-1" value={actionForm.title} onChange={e => setActionForm({...actionForm, title: e.target.value})} data-testid="action-title-input" /></div>
              <div><Label>Description</Label><Textarea className="mt-1" rows={2} value={actionForm.description} onChange={e => setActionForm({...actionForm, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={actionForm.priority} onChange={e => setActionForm({...actionForm, priority: e.target.value})}>
                    {['critical', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div><Label>Due Date</Label><Input type="date" className="mt-1" value={actionForm.due_date} onChange={e => setActionForm({...actionForm, due_date: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Assigned To</Label><Input className="mt-1" value={actionForm.assigned_to_name} onChange={e => setActionForm({...actionForm, assigned_to_name: e.target.value})} /></div>
                <div><Label>Department</Label><Input className="mt-1" value={actionForm.department} onChange={e => setActionForm({...actionForm, department: e.target.value})} /></div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowActionForm(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreateAction} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-action-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Add Action
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailPage;
